// One continuous dark section directly under the hero.
// Background is transparent — the PageField canvas in Home.jsx provides
// the #070808 backdrop, ambient dust, hero glow, and Trusted bloom.
// No DustCanvas, no Bloom, no grain here.

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Crosshair, Bot, Cpu, Server, Sparkles } from "lucide-react";
import OrbitServicesSlider from "./services-orbit/OrbitServicesSlider";

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER_CLIENTS = [
  { id: "p1", name: "Acme Corp",    displayMode: "name", colorMode: "bw" },
  { id: "p2", name: "NovaTech",     displayMode: "name", colorMode: "bw" },
  { id: "p3", name: "Vertex AI",    displayMode: "name", colorMode: "bw" },
  { id: "p4", name: "Nexus Labs",   displayMode: "name", colorMode: "bw" },
  { id: "p5", name: "Orion Group",  displayMode: "name", colorMode: "bw" },
];

const SERVICES = [
  {
    Icon: Crosshair,
    title: "Strategy",
    desc: "AI roadmaps, architecture reviews, and guidance at any stage.",
    related: ["AI Consulting", "AI Product Management"],
  },
  {
    Icon: Bot,
    title: "Build AI",
    desc: "End-to-end AI products and multi-agent automation networks.",
    related: ["AI Development", "Multi-Agent Systems", "Agentic OS"],
  },
  {
    Icon: Cpu,
    title: "Train & Deploy",
    desc: "Custom models, inference pipelines, and AI architecture.",
    related: ["ML Solutions", "AI Architecture"],
  },
  {
    Icon: Server,
    title: "Run at Scale",
    desc: "Cloud, data, system architecture, and performance at scale.",
    related: ["Cloud Infrastructure", "System Architecture", "Performance Engineering"],
  },
  {
    Icon: Sparkles,
    title: "Claude AI Enablement",
    // Only shown on responsive/mobile cards (OrbitTile picks this over
    // `title` there) — desktop keeps the full "Claude AI Enablement".
    titleMobile: "AI Enablement",
    desc: "Turn Claude into a real production capability across the business",
    related: ["Custom Skills", "Cowork & MCP", "Claude Code"],
  },
];

// ── Partner logo — same data shape as the clients API ─────────────────────────

function ClientLogo({ client }) {
  const [hovered, setHovered] = useState(false);
  const { displayMode = "name", colorMode = "bw", name, logoData, iconData } = client;

  const imgFilter  = colorMode === "bw" ? "brightness(0) invert(1)" : "none";
  const imgOpacity = hovered ? 0.72 : 0.46;
  const imgStyle   = {
    objectFit: "contain", filter: imgFilter,
    opacity: imgOpacity, transition: "opacity .22s ease",
  };
  const nameColor = hovered ? "rgba(214,228,226,0.78)" : "rgba(200,212,210,0.46)";

  const logo   = logoData ? <img src={logoData} alt={name || ""} style={{ ...imgStyle, height: "40px", width: "auto" }} /> : null;
  const icon   = iconData ? <img src={iconData} alt=""           style={{ ...imgStyle, height: "36px", width: "36px" }} /> : null;
  const nameEl = name ? (
    <span style={{
      fontFamily: "Montserrat, sans-serif", fontSize: "16px", fontWeight: 600,
      letterSpacing: "0.04em", color: nameColor, whiteSpace: "nowrap",
      transition: "color .22s ease",
    }}>{name}</span>
  ) : null;

  let content;
  if      (displayMode === "logo")      content = logo || nameEl || null;
  else if (displayMode === "icon")      content = icon || nameEl || null;
  else if (displayMode === "name")      content = nameEl || null;
  else if (displayMode === "icon_name") content = <React.Fragment>{icon}{nameEl}</React.Fragment>;
  else if (displayMode === "logo_name") content = <React.Fragment>{logo || icon}{nameEl}</React.Fragment>;
  else                                  content = logo || icon || nameEl || null;

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {content}
    </div>
  );
}

// ── Main section ───────────────────────────────────────────────────────────────

const TrustedServicesFlow = () => {
  const [clients,  setClients]  = useState(PLACEHOLDER_CLIENTS);

  const servicesHeadingRef = useRef(null); // container div with h2 + p as direct children
  const tealTitleRef       = useRef(null); // the "Services" h2

  // Heading: stagger h2 → p with teal glow pulse on "Services"
  // Uses IntersectionObserver — ScrollTrigger miscalculates on dynamic viewports
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !servicesHeadingRef.current) return;
    const el = servicesHeadingRef.current;
    const children = Array.from(el.children);
    gsap.set(children, { opacity: 0, y: 28 });
    let tween;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tween = gsap.to(children, {
            opacity: 1, y: 0,
            stagger: 0.13, duration: 0.72, ease: "power3.out",
            onComplete: () => {
              setTimeout(() => {
                if (tealTitleRef.current) {
                  gsap.fromTo(tealTitleRef.current,
                    { textShadow: "0 0 0px transparent" },
                    { textShadow: "0 0 22px rgba(123,181,177,0.55)", duration: 0.4, yoyo: true, repeat: 1, ease: "power2.inOut" });
                }
              }, 420);
            },
          });
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); if (tween) tween.kill(); gsap.set(children, { clearProps: "opacity,y" }); };
  }, []);

  // Fetch real client logos; fall back to placeholders silently
  useEffect(() => {
    axios.get(`${API}/clients`)
      .then((res) => {
        if (res.data?.success && res.data?.data?.length > 0) {
          setClients(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="services"
      style={{ position: "relative", width: "100%", background: "transparent", zIndex: 3 }}
    >
      <div className="tsf-inner" style={{
        position: "relative", zIndex: 3,
        maxWidth: 1240, margin: "0 auto",
        padding: "132px clamp(24px, 4vw, 56px) 110px",
      }}>

        {/* ── Trusted Partners ──────────────────────────────────────────── */}
        <div style={{ textAlign: "center" }}>
          <h2 style={{
            margin: 0,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: "clamp(34px, 7vw, 56px)",
            lineHeight: 1.18,
            letterSpacing: "-0.02em",
          }}>
            <span style={{ color: "#FFFFFF" }}>Trusted by experts.</span>
            <br />
            <span style={{ color: "#7BB5B1" }}>Used by the leaders.</span>
          </h2>

          {/* Infinite marquee — logo strip scrolls left continuously */}
          <style>{`@keyframes marquee-ltr { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
          <div style={{
            marginTop: 64,
            overflow: "hidden",
            maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
          }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "clamp(48px, 7vw, 80px)",
                width: "max-content",
                animation: "marquee-ltr 28s linear infinite",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.animationPlayState = "paused"; }}
              onMouseLeave={(e) => { e.currentTarget.style.animationPlayState = "running"; }}
            >
              {[...clients, ...clients].map((c, idx) => (
                <ClientLogo key={`${c.id || c._id}-${idx}`} client={c} />
              ))}
            </div>
          </div>
        </div>

        {/* ── Fade transition zone — dust visibly thins out here ─────────── */}
        <div className="tsf-fade-zone" style={{ height: 300 }} />

        {/* ── Services ──────────────────────────────────────────────────── */}
        <div>
          <div ref={servicesHeadingRef} style={{ textAlign: "center" }}>
            <h2 ref={tealTitleRef} style={{
              margin: 0,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 300,
              fontSize: "clamp(34px, 8vw, 60px)",
              lineHeight: 1.0,
              letterSpacing: "-0.025em",
              color: "#FFFFFF",
            }}>Services</h2>
            <p className="tsf-services-subtitle" style={{
              margin: "20px 0 0",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 300, fontSize: 19, letterSpacing: "0.002em",
              color: "rgba(225,232,231,0.50)",
            }}>From AI products to cloud infrastructure.</p>
          </div>

          <div style={{ marginTop: 96 }}>
            <OrbitServicesSlider services={SERVICES} />
          </div>
        </div>

      </div>
    </section>
  );
};

export default TrustedServicesFlow;
